// Utilidades para procesar y agrupar intentos de puja
// Extraído de BiddingAttemptTimeline para reutilización

/**
 * Agrupar eventos de engagement en intentos de puja coherentes
 * @param {Array} events - Eventos de engagement (filtrados a bidding events)
 * @returns {Array} - Array de intentos procesados
 */
export const groupBiddingAttempts = (events) => {
  const attempts = [];
  let currentAttempt = null;

  // Ordenar eventos cronológicamente
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );

  sortedEvents.forEach(event => {
    const eventType = event.event_type;

    // Iniciar nuevo intento
    if (eventType === 'bid_form_focus' || 
        (eventType === 'bid_amount_changed' && !currentAttempt)) {
      
      if (currentAttempt) {
        // Cerrar intento anterior como abandonado
        currentAttempt.status = 'abandoned';
        currentAttempt.reason = 'Inició nuevo intento';
        attempts.push(currentAttempt);
      }

      currentAttempt = {
        startedAt: event.created_at,
        amounts: [],
        amountChanges: 0,
        conditions: [],
        conditionChanges: 0,
        customText: null,
        errors: [],
        status: 'in_progress',
        reason: null,
        timeSpent: 0,
        events: [event],
      };
    }

    // Agregar evento a intento actual
    if (currentAttempt) {
      currentAttempt.events.push(event);

      // Procesar según tipo de evento
      switch (eventType) {
        case 'bid_amount_changed':
          // Ahora solo se trackea cuando amount >= 10,000
          currentAttempt.amounts.push(event.event_data?.amount);
          currentAttempt.amountChanges = event.event_data?.changeCount || currentAttempt.amountChanges + 1;
          break;

        // bid_conditions_interact ya no genera eventos individuales
        // Los cambios se consolidan en bid_submit_success/bid_verification_email_sent

        case 'bid_custom_conditions_written':
          currentAttempt.customText = true;
          break;

        case 'bid_submit_attempt_failed':
          currentAttempt.errors.push({
            type: event.event_data?.errorType,
            message: determineErrorMessage(event.event_data?.errorType),
          });
          currentAttempt.status = 'failed';
          currentAttempt.reason = determineErrorMessage(event.event_data?.errorType);
          currentAttempt.timeSpent = event.event_data?.timeInForm || 0;
          attempts.push(currentAttempt);
          currentAttempt = null;
          break;

        // Legacy: bid_completed (históricos)
        case 'bid_completed':
          currentAttempt.status = 'success';
          currentAttempt.reason = 'Oferta completada exitosamente';
          currentAttempt.finalAmount = event.event_data?.amount;
          currentAttempt.timeSpent = event.event_data?.totalTimeFromFormFocus || 0;
          attempts.push(currentAttempt);
          currentAttempt = null;
          break;

        // Nuevo: Email enviado (pendiente verificar)
        case 'bid_verification_email_sent':
          currentAttempt.status = 'pending_verification';
          currentAttempt.reason = 'Email enviado, pendiente verificación';
          currentAttempt.finalAmount = event.event_data?.amount;
          currentAttempt.timeSpent = event.event_data?.totalTimeFromFormFocus || 0;
          // NO cerramos el intento aquí - esperamos bid_verified o bid_intent_expired
          break;

        // Nuevo: Puja verificada exitosamente
        case 'bid_verified':
          currentAttempt.status = 'success';
          currentAttempt.reason = 'Oferta verificada y confirmada';
          currentAttempt.finalAmount = event.event_data?.amount;
          currentAttempt.verifiedAt = event.created_at;
          attempts.push(currentAttempt);
          currentAttempt = null;
          break;

        // Nuevo: Intent expirado
        case 'bid_intent_expired':
          currentAttempt.status = 'expired';
          currentAttempt.reason = 'Verificación expirada';
          attempts.push(currentAttempt);
          currentAttempt = null;
          break;

        case 'bid_confirm_modal_abandoned':
          currentAttempt.status = 'abandoned';
          currentAttempt.reason = 'Abandonó popup de confirmación';
          attempts.push(currentAttempt);
          currentAttempt = null;
          break;

        // bid_form_blur ya no se trackea - eliminado en optimización de eventos
      }
    }
  });

  // Si quedó un intento sin cerrar, agregarlo según su estado
  if (currentAttempt) {
    if (currentAttempt.status === 'pending_verification') {
      // Se envió email pero no verificó - mantener status para el estilo
      currentAttempt.reason = 'Sin verificar en el email';
    } else {
      // Abandonó antes de completar el proceso
      currentAttempt.status = 'abandoned';
      currentAttempt.reason = 'Abandonó sin ofertar';
    }
    attempts.push(currentAttempt);
  }

  return attempts;
};

/**
 * Determinar mensaje de error amigable según tipo
 * @param {string} errorType - Tipo de error de validación
 * @returns {string} - Mensaje legible
 */
export const determineErrorMessage = (errorType) => {
  const messages = {
    'amount_too_low': 'Cantidad inferior al mínimo',
    'amount_too_high': 'Cantidad demasiado alta',
    'increment_too_small': 'Incremento muy pequeño',
    'bidding_not_started': 'Pujas aún no iniciadas',
    'bidding_closed': 'Pujas cerradas',
  };
  return messages[errorType] || 'Error de validación';
};

/**
 * Filtrar solo intentos NO exitosos (para sección de intentos pendientes)
 * @param {Array} attempts - Array de intentos procesados
 * @returns {Array} - Solo intentos abandonados, fallidos, expirados o pendientes
 */
export const filterUnsuccessfulAttempts = (attempts) => {
  return attempts.filter(a => a.status !== 'success');
};

/**
 * Obtener configuración visual según estado del intento
 * @param {string} status - Estado del intento
 * @returns {Object} - { icon, bg, text, label }
 */
export const getAttemptStatusConfig = (status) => {
  const configs = {
    'abandoned': {
      icon: '🟡',
      bg: 'bg-red-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-400',
      label: 'Abandonado',
    },
    'failed': {
      icon: '❌',
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      text: 'text-red-400',
      label: 'Fallido',
    },
    'expired': {
      icon: '⏰',
      bg: 'bg-gray-500/10',
      border: 'border-gray-500/30',
      text: 'text-gray-400',
      label: 'Expirado',
    },
    'pending_verification': {
      icon: '📧',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-400',
      label: 'Pendiente',
    },
    'success': {
      icon: '✔',
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      text: 'text-green-400',
      label: 'Completado',
    },
    'in_progress': {
      icon: '🔄',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-400',
      label: 'En curso',
    },
  };

  return configs[status] || configs['abandoned'];
};

/**
 * Formatear cantidad en euros
 * @param {number} amount - Cantidad a formatear
 * @returns {string} - Formato: €XXX.XXX
 */
export const formatAmount = (amount) => {
  if (!amount) return null;
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export default {
  groupBiddingAttempts,
  determineErrorMessage,
  filterUnsuccessfulAttempts,
  getAttemptStatusConfig,
  formatAmount,
};